/**
 * there isn't enough going on in here to write unit tests for, at this time
 */

import {
  CASStatus,
  CustomFieldClassTypeItem,
  DSStatus,
  FDCConcurrenceLevelSideEffect,
  FieldDesignChangeStatus,
  FrequencyItem,
  IFIReportTypeItem,
  NotificationEventItem,
  Permission,
  SharePointParentFolder,
  googleSheetURLFormat,
} from '@cims-online/shared'
import {
  CasRoutingGroup,
  DsDiscipline,
  FdcApprovalGroup,
  FdcConcurrenceGroup,
} from 'schema'
import { RFIResponseDiscipline } from '../App/components/ProjectRoutes/components/RequestsForInformation/components/ResponseDisciplines/ResponseDisciplinesTableContext'

const ConcreteMixDesign = {
  Status: 'Current workflow status of Mix Design',
  Number: 'Mix design number of placed concrete',
  ExpirationDate: 'Expiration date of mix design',
  Source: 'Source of mix design batch',
  Twenty8Strength: '28 day break strength the mix was design for',
  Air: 'Air range of mix design',
  Slump: 'Slump range of mix design',
  WC: 'Water and cement ratio of mix design',
  Class: 'Use class of mix design',
  Use: 'Allowable uses of mix design',
  MaxAggSize: 'Maximum aggregate size of mix design',
  Aggregates: 'Aggregate sources used in mix design',
  Admixtures: 'Admixtures used in mix design',
  SubmittalID: 'Submittal ID the mix design was submitted under',
} as const

/**
 * help messages for use throughout the application
 */
export const HelpMessage = {
  ExternalLinkEditor:
    'Select, and customize, Quick Links or add custom External Links',
  CustomFieldEditor: ({ type }: CustomFieldClassTypeItem) =>
    `Edit Custom Field values associated with the ${type}` as const,
  Name: 'A short, human readable, name',
  Description: (entity: string) =>
    `A longer description to help describe this ${entity} throughout CIMS` as const,
  ExternalLinks: (options?: { search?: boolean }) =>
    `External Hyperlinks associated with the record${
      options?.search ? '. Search results will include labels and URLs' : ''
    }` as const,
  PayItemsBasicField: (object: string) =>
    `Pay Items associated with the ${object}` as const,
  UnlockSharePointFolder:
    'Unlock allows users to open up an associated SharePoint folder for editing',
  LockSharePointFolder:
    'Lock closes the associated SharePoint folder from editing',
  CIMSTable: {
    Active: 'Removed records are inactive',
  } as const,
  Exports: {
    Options: 'Configured options to filter results',
    Name: (type: 'report' | 'photo') =>
      `The name of the ${type === 'photo' ? 'record type' : type}` as const,
    Template: (type: 'report' | 'photo') =>
      `The filename template that will be used for each ${
        type === 'photo' ? 'record type' : type
      }` as const,
    Example:
      'An example of what the Template might look like in practice. Missing values are skipped.',
  } as const,
  Notifications: {
    Every: (
      { name }: Pick<NotificationEventItem, 'name'>,
      object:
        | 'Concrete Test Set'
        | 'Construction & Admin Submittal'
        | 'Field Design Change'
        | 'Design Submittal'
        | 'Request For Information'
        | 'Issue'
        | 'Identified Field Issue'
        | 'Engineering Judgement'
    ) =>
      `Recipients receive an email for every ${name} event, on every ${object}` as const,
  } as const,
  PayItems: {
    Summary: {
      GoogleSheet: {
        Headers: 'Configure the visibility and order for each header',
      } as const,
    } as const,
    Groups: 'Used to group similar connections together in the Summary',
    Manage: {
      Internals: {
        Modules: 'CIMS modules with Pay Items',
      } as const,
      Connections: {
        Source: "The connection's data source",
        LastAttemptedDate: 'The date a sync was last attempted',
        LastSyncDate: 'The date a sync was last successful',
        LastSyncError:
          'An error encountered during the last sync. Cleared after a successful sync',
        GoogleSheet: {
          Headers: 'Headers as they will appear in the Summary',
          DocumentId: `Google's unique identifier for the Google Sheet document. This is extracted from a Google Sheet URL (${googleSheetURLFormat})`,
          SheetId: `A number used to access a specific sheet inside a Google Sheet document. This is extracted from a Google Sheet URL (${googleSheetURLFormat})`,
          PayItemColumnKey:
            'The name of the column where one or more Pay Items can be found. Cells become headers if they are in the same row as this key, and following rows become the data. Multiple Pay Items can be comma, or new line (ALT + Enter), separated',
          ImportWith: 'Select method of import',
        } as const,
      } as const,
      Groups: {
        Connections: 'Connections and Modules associated with the group',
      } as const,
    } as const,
  } as const,
  Issues: {
    Notifications: {
      FrequencyItem: 'The frequency which you want to be sent emails',
      dailyItem: 'The day of the week which you want to be sent emails',
      ByIssueType: ({ name }: NotificationEventItem) =>
        `Recipients receive an email for ${name} events for Issues with a specific Type` as const,
    },
    DispositionTypes: {
      NonApplicableIssueTypes:
        'Types added to this list will prevent this Disposition Type from being selected when the Type is set on an Issue',
    },
    IssueTypes: {
      NonApplicableDispositionTypes:
        'Disposition Types added to this list will not be available when their associated Type is set on an Issue',
    },
    IssueTracker: {
      Status: 'The current workflow status of the Issue',
      Number:
        'A generated, sequential number to identify this Issue in the Project',
      Types:
        'Types associated with the Issue. Dictate which Disposition Types are available for selection',
      Description: 'Some text regarding the Issue',
      SubmittedBy: 'The User who submitted the Issue',
      SubmittedDate: 'The date the Issue was submitted',
      Response: 'Some text regarding the description',
      ResponseBy: 'The User who responded to the Issue',
      ResponseDate: 'The date the response was given',
      Disposition: 'The Disposition Type associated with the Issue',
      DispositionNotes: 'Some text regarding the Disposition',
      DispositionBy: 'The User who gave the Disposition',
      DispositionDate: 'The date the disposition was given',
      ClosureNotes: 'Some text regarding the closure of the Issue',
      ClosedBy: 'The User who closed the Issue',
      ClosedDate: 'The date the Issue was closed',
      PayItems: 'All Pay Items associated with the Issue',
      Reports: 'All Reports associated with the Issue',
    },
  } as const,
  FieldDesignChanges: {
    FDCTracker: {
      Status: 'The current workflow status of the Field Design Change',
      Number:
        'A generated, sequential number to identify this Field Design Change in the Project',
      Disciplines:
        'Disciplines which should review the Field Design Change before approval',
      Issue:
        'A full, detailed, description for why the Field Design Change is needed',
      ConcurrenceLevel: `The level determines which Approval Groups are required during ${FieldDesignChangeStatus.PendingApprovals.name}`,
      ConcurrenceGroups: `Each group must be concurred on, or rejected, during ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
      Approvals: `Each Approval Group associated with the Field Design Change by its level, must be approved before it can be ${FieldDesignChangeStatus.Closed.name}`,
      Location:
        'Locations, stations, and offsets where the Field Design Change is applicable',
      SpecsAffected:
        'Spec sections and sub-sections applicable to Field Design Change work',
      DrawingsAffected:
        'Plan sheets where Field Design Change deviates from design',
      RequestedApprovalDate: 'Date requested for Field Design Change approval',
      Resolution: 'Full, detailed, changes from design, to resolve the issue',
      Revert: `Revert allows for reversal of the status to ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
      RequestedBy: 'The User who submitted the Field Design Change',
      SubmittedDate: `The date the Field Design Change went from ${FieldDesignChangeStatus.Draft.name} to ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
      DesignSubmittals: `Design Submittals are typically linked to a Field Design Change through ${FDCConcurrenceLevelSideEffect.EarlyClosure.name}`,
    } as const,
    Concurrences: {
      Levels: {
        Name: 'Name to display in Level column of FDC Tracker Table',
        SideEffect:
          'Side Effects trigger as soon as a Field Design Change is saved',
        ApprovalGroups:
          'The Concurrence Level determines which Approval Groups are applied to a Field Design Change after moving to Pending Approvals',
      } as const,
      Groups: {
        Name: 'Name of Group required to concur on FDC Levels',
        Concurrers: `Concurrers are authorized to concur on, or reject, the Concurrence Level of a Field Design Change on behalf of their Concurrence Group during ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
        AssociatedFDCs:
          'Field Design Changes that have been concurred on by this Concurrence Group',
      } as const,
    } as const,
    ApprovalGroups: {
      Name: 'Name of Group required to approve FDCs',
      ConcurrenceLevels:
        'Approval Groups are automatically applied to a Field Design Change, based on its Concurrence Level, when moved to Pending Approvals',
      Approvers: `Approvers are authorized to approve their Approval Group on any Field Design Change associated with the group during ${FieldDesignChangeStatus.PendingApprovals.name}`,
    } as const,
    Disciplines: {
      Name: 'Name of Discipline to assign to FDCs',
      AssociatedFDCs:
        'Field Design Changes that have been assigned this Discipline',
    } as const,
    Notifications: {
      ByConcurrenceLevel: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Field Design Changes with a specific Concurrence Level` as const,
      ApprovalGroups: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events with a specific Approval Group or combination of Approval Group and Concurrence Level` as const,
      ConcurrenceGroups: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events with a specific Concurrence Group or combination of Concurrence Group and Concurrence Level` as const,
      Group: (name: string, event: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${event.name} events for ${name}` as const,
      GroupLevel: (group: string, event: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${event.name} events for ${group} and a specific Concurrence Level` as const,
      ApprovalGroupTags: (approvalGroup?: Pick<FdcApprovalGroup, 'name'>) =>
        `Roles and individual Users associated with ${
          approvalGroup?.name ?? 'all Approval Groups'
        }` as const,
      ConcurrenceGroupTags: (
        concurrenceGroup?: Pick<FdcConcurrenceGroup, 'name'>
      ) =>
        `Roles and individual Users associated with ${
          concurrenceGroup?.name ?? 'all Concurrence Groups'
        }` as const,
      Fields: 'Modifying any of the these fields will trigger the event',
    } as const,
  } as const,
  RequestsForInformation: {
    RFITracker: {
      Status: 'The current workflow status of the Request For Information',
      Number:
        'A generated, sequential number to identify this Request For Information in the Project',
      Description:
        'This is the Request For Information. This is the question that needs to be answered. (Note: If cut short, view full text in expandable section)',
      FullDescription:
        "The full text for the RFI's description, the question that needs to be answered.",
      Organization: 'Organization of user requesting the information',
      SubmittedBy: 'User who created the RFI',
      ResponseDate:
        'Requested date for response (Note: Contractual date may differ)',
      ResponseDisciplines:
        'Disciplines which should respond to the request (Note: View response comments in expandable section)',
      ReopenDisciplines:
        'Select discipline(s) to reopen. Note: at least one reopen discipline or one add discipline is required.',
      AddDisciplines:
        'Select discipline(s) to add. Note: at least one reopen discipline or one add discipline is required.',
      Response: 'Body of the response to the RFI',
      Responses: 'Date, user, and response comments for each discipline',
      CloseDisciplineResponse:
        'Close the response for this Response Discipline. Closing all Disciplines will close the RFI.',
      SubmittedDate: 'Date RFI was sent to disciplines for review/response',
      ClosedDate: 'Date last discipline marked their groups response closed',
    } as const,
    Organizations: {
      Name: 'Organizations who will be submitting Requests for Information',
      AssociatedRFIs: 'RFIs the organization has submitted for response',
    } as const,
    ResponseDisciplines: {
      Name: 'Name of discipline which can be assigned to respond to an RFI',
      Responders:
        'Users who are allowed to submit a response for the discipline',
      AssociatedRFIs: 'RFIs the discipline has been assigned to review',
    } as const,
    Notifications: {
      ByResponseDiscipline: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Requests For Information with a specific Response Discipline` as const,
      Responders: ({ name }: Pick<RFIResponseDiscipline, 'name'>) =>
        `Users associated with ${name}`,
    } as const,
  } as const,
  CAS: {
    DispositionTypes: {
      RequiresComments:
        'Require comments to be added in addition to the disposition',
    },
    SubmittalTracker: {
      Status: 'The current workflow status of the submittal',
      RoutingGroups: 'Groups which should review the submittal before approval',
      PackageNumber: 'Submittal package identification number',
      PackageName: 'Full package name, excluding package identification number',
      Type: 'Main submittal type',
      Subtype: 'Subtype to main submittal type',
      SubmittedBy: 'User who created the submittal',
      CreatedDate: 'Date submittal was created',
      Quantity: 'Quantity of submittal items in package',
      Units: 'Units of submittal quantity',
      RequestedReviewDate:
        'Requested date for completion of review (Note: Contractual review date may differ)',
      ContractualReviewDate: 'Contractual date review must be completed by',
      Disposition: 'Final disposition of submittal',
      DispositionDate: 'Date disposition was submitted',
      DispositionBy: 'User who submitted disposition',
      BuyAmericaQuantity:
        'Quantity of submittal applicable to buy America requirement',
      BuyAmerica:
        'Checked indicates submittal applicable to buy America requirement',
      PayItems: 'All pay items associated with submittal',
      Edit: 'Edit allows user to edit submittal data',
      Remove: 'Remove deletes submittal from log',
      Restore: 'Restore will restore a deleted submittal',
      Revert: 'Revert will move a submittal to the previous workflow stage',
    } as const,
    RoutingGroups: {
      Name: 'Discipline groups which should review the submittal before approval',
      Approvers: `Approvers are authorized to approve their Routing Group on any Construction & Admin Submittal associated with the group during ${CASStatus.GroupReview.name}`,
      AssociatedSubmittals:
        'Submittals the routing group has been assigned to review',
      Details: `Approvers are authorized to approve their Routing Group on any Construction & Admin Submittal associated with the group during ${CASStatus.GroupReview.name}. Users with the ${Permission.ReviewAllConstructionAdminSubmittal.name} permission can check off any Routing Group during ${CASStatus.GroupReview.name} and need not be an Approver.`,
    } as const,
    Types: {
      Name: 'Construction & Admin Submittal Main Group Types',
      AssociatedSubmittals: 'Submittals that have been assigned this type',
    } as const,
    SubTypes: {
      Name: 'Construction & Admin Submittal Subgroup Types',
      AssociatedSubmittals: 'Submittals that have been assigned this subtype',
    } as const,
    Notifications: {
      ByRoutingGroup: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Construction & Admin Submittals with a specific Routing Group` as const,
      Approvers: (group: Pick<CasRoutingGroup, 'name'>) =>
        `Users associated with ${group.name}`,
      RequestedReviewSettings:
        'Settings for when a notification will go out reminding people of an upcoming review date',
      Frequency:
        'The time of day the Requested Review Upcoming summary should be sent',
      NotificationThreshold:
        'The number of days before the Requested Review Date to consider a submittal upcoming',
    } as const,
  } as const,
  DesignSubmittals: {
    Log: {
      Status: 'The current workflow status of the submittal',
      PackageNumber: 'Submittal package identification number',
      PackageName: 'Full package name, excluding package identification number',
      Type: 'The submittal type. For example: 30%, 60%, 90%, or RFC',
      Disciplines: 'Disciplines which should review the submittal',
      Actions:
        'The purpose of review. For example: Informational, Approval, or Comment Closure',
      SubmittedBy: 'User who created the submittal',
      SubmittedDate: 'Date submittal was created',
      SubmittingOrg: 'Organization submittal was created by',
      RequestedActionDate:
        'Requested date for completion of review (Note: Contractual review date may differ)',
      ContractDueDate: 'Contractual date review must be completed by',
      Disposition: 'Final disposition of submittal',
      DispositionDate: 'Date disposition was submitted',
      DispositionBy: 'User who submitted disposition',
      PayItems: 'All pay items associated with submittal',
      FDCs: 'Field design changes that prompted the design submittal',
    } as const,
    Types: {
      Name: 'Design Submittal Main Group Types',
      AssociatedSubmittals:
        'Design Submittals that have been assigned this type',
    } as const,
    Organizations: {
      Name: 'Organizations who will be submitting Design Submittals',
      AssociatedSubmittals: 'Design Packages Submitted By Organization',
    } as const,
    Disciplines: {
      Name: 'Disciplines groups which should review the submittal',
      Approvers: `Approvers are authorized to approve their Discipline on any Design Submittal associated with the discipline during ${DSStatus.DisciplineAction.name}`,
      AssociatedSubmittals:
        'Submittals the discipline group has been assigned to review',
      Details: `Approvers are authorized to approve their Discipline on any Design Submittal associated with the discipline during ${DSStatus.DisciplineAction.name}. Users with the ${Permission.ReviewAllDesignSubmittal.name} permission can check off any Routing Group during ${DSStatus.DisciplineAction.name} and need not be an Approver.`,
    } as const,
    Actions: {
      Name: 'Review Actions Requested. For example: Informational, Approval, or Comment Closure',
      AssociatedSubmittals: 'Design Packages submitted with this Action',
    } as const,
    Notifications: {
      ByDiscipline: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Design Submittals with a specific Discipline` as const,
      Approvers: (group: Pick<DsDiscipline, 'name'>) =>
        `Users associated with ${group.name}`,
    } as const,
  } as const,
  CustomWorkflows: {
    Records: {
      Notes:
        'Notes entered by Approval Group approvers when they approve or reject a Technical Direction',
    } as const,
    Permissions: {
      Permission:
        'The workflow action this permission grants. Some permissions require conditions.',
      Fields: 'Limits field editing to the selected workflow fields',
      ApprovalStatus:
        'The approval status users can advance or fast track from',
      Statuses:
        'The workflow statuses where this permission applies. For approval permissions, these are the statuses users can advance or fast track to.',
      Users: 'Users granted this permission',
      AddUsers:
        'Users selected here will be added to every selected permission without removing existing users.',
      Columns: {
        Permission: 'Workflow permission granted by this row',
        Conditions:
          'Conditions narrow where this permission applies, such as which fields can be edited, which approval status can be advanced, or which workflow statuses allow the action.',
        Users: 'Users granted this permission',
      } as const,
    } as const,
  } as const,
  Concrete: {
    ConcreteTestSetTracker: {
      Tests: {
        TestID: 'ID of test within test set',
        PercentAir: 'Tested percent air in concrete',
        Slump: 'Tested slump of concrete',
        AirTemp: 'Ambient air temperature at time of placement',
        ConcreteTemp: 'Tested temperature of concrete at time of placement',
        UnitWeight: 'Tested unit weight of concrete',
        Twenty8DayAvg: 'Average 28 day break strength of cylinders',
        Notes: 'Concrete Test Notes',
        SampledAt:
          'The truck the sample was taken from or approximate quantity sampled',
        BatchTime: 'Time concrete was batched at plant',
        SampledTime: 'Time sample was taken from truck',
        SampleLocation: 'Station and offset where sample was taken',
        Alignment: 'Alignment associated with sample location',
        IASampleWith: 'Person IA sample was sampled against',
        AirMeter: 'ID of air meter',
        CalibrationDate: 'Date air meter was calibrated',
        AirTestedBy: 'The technician who tested the percent air',
        SlumpTestedBy: 'The technician who tested the slump',
        UnitWeightTestedBy: 'The technician who tested the unit weight',
        OverrideComments:
          'Override comments to close the test when a failure in field testing occurs',
      },
      CoresTable: {
        CoreID: 'ID of concrete core',
        CoreLocation: 'Station and offset where core was taken',
        Alignment: 'Alignment associated with core location',
        ReqThickness: 'Required thickness of core',
        AvgThickness: 'Average measured thickness of core',
        Status: 'Status of core',
        Notes: 'Field notes about core',
        MeasuredBy: 'Technician who measured core',
        MeasuredDate: 'Date core was measured',
        Thickness1: 'Core thickness measurement 1',
        Thickness2: 'Core thickness measurement 2',
        Thickness3: 'Core thickness measurement 3',
        Thickness4: 'Core thickness measurement 4',
      },
      Placements: {
        PlacementID: 'ID of concrete placement',
        StartLocation: 'Start station and offset of placement',
        StartAlignment: 'Alignment associated with start location',
        EndLocation: 'End station and offset of placement',
        EndAlignment: 'Alignment associated with end location',
        SteelCheck: 'Result of steel check',
        CureCoverage: 'Result of cure coverage',
        Notes: 'Field notes about placement',
        CubicYards: 'Cubic yards placed',
        LinealFeet: 'Lineal feet of placement',
        SquareFeet: 'Square feet of placement',
        SquareYards: 'Square yards of placement',
        RequiredThickness: 'Required thickness of placement',
        CureGallons: 'Gallons of cure used on placement',
        CureSqFt: 'Square footage where cure was applied',
        RequiredCureRate: 'Required rate cure should be applied in SqFt/Gal',
        CureRate: 'Application rate of cure on placement in SqFt/Gal',
        ContractorName: 'Contractor who performed the placing of concrete',
        SteelNotes: 'Notes about steel check',
      },
      MixDesign: {
        ...ConcreteMixDesign,
      },
      Material: {
        PlacementType: 'Placement type of concrete test set',
        SpecBreakStrength: 'Break strength specification of placement type',
        MaxBreakStrength:
          'Maximum break strength of placement type before considered failure',
        MinBreakStrength:
          'Minimum break strength of placement type before considered failure',
        TestingFrequency:
          'Required frequency of testing for air, slump, unit weight, concrete temp, and air temp',
        CylinderFrequency:
          'Required frequency of casting for cylinders and cubes',
        CoreFrequency: 'Required frequency of cores',
      },
      TestSetID: 'The human readable ID of the Concrete Test Set',
      Date: 'Date concrete was placed',
      Status: 'Current status of the Concrete Test Set',
      DocumentChecklist:
        'Checklist to document completion of items related to the Concrete Test Set',
      Cores: '28 day break strengths of Core Measurements',
      Cylinders: '28 day break strengths of Concrete Tests',
      BreakData: {
        Core: {
          Age: 'Break age of core when broken',
          BreakDate: 'Date core was broken',
          Strength: 'Break strength of core in PSI',
          TestingLab: 'Lab where core was broken',
          LabTech: 'Technician who broke core',
          LabNumber: 'Lab number assigned to core',
          Date: 'Day the concrete was placed',
          Diameter: 'Diameter of core',
          PeakLoad: 'Peak load applied to core',
          Type: 'The type of break the core experienced',
        },
        Cylinder: {
          Age: 'Break age of sample when broken',
          BreakDate: 'Date sample was broken',
          Strength: 'Break strength of sample in PSI',
          TestingLab: 'Lab where sample was broken',
          LabTech: 'Technician who broke sample',
          LabNumber: 'Lab number assigned to break sample',
          Date: 'Date cylinders were cast',
          Diameter: 'Diameter of cylinder or size of cube',
          PeakLoad: 'Peak load applied to sample',
          Type: 'The type of break the sample experienced',
        },
      } as const,
      Packet: 'Link to the PDF packet of the Concrete Test Set',
      Actions: `Upload files to the ${SharePointParentFolder.ConcretePackets.name} folder in SharePoint and create new packets`,
    } as const,
    DocumentChecklists: {
      Name: 'Name displayed in Document Checklist column',
      AssociatedTestSets: 'Test Sets where checklist item has been checked',
      PlacementTypes: 'Placement Types which use Checklist Item',
    } as const,
    LabTechs: {
      LabTechs: {
        User: (options?: { header: true }) =>
          options?.header
            ? 'A CIMS User associated with this Lab Tech'
            : 'A User to make a Lab Tech',
        TestingLabs: (name: string) =>
          `Testing Labs where ${name} is a member` as const,
      } as const,
      TestingLabs: {
        LabTechs: 'Generic, or User, Lab Techs associated with the Testing Lab',
      },
    } as const,
    Notifications: {
      ByConcreteMaterial: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Concrete Test Sets with a specific Concrete Material` as const,
      ByConcreteMixDesign: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for Concrete Test Sets with a specific Concrete Mix Design` as const,
    } as const,
  } as const,
  Administration: {
    Components: {
      ClearPermissions:
        'Clears only additional permissions. Permissions assigned by role(s) will remain unchanged.',
      ClearPermissionsDisabled: 'No permissions assigned',
      ClearAdditionalPermissionsDisabled: 'No additional permissions assigned',
      Templates:
        'Copies/adds all permissions from a Role Template into the current configuration',
    } as const,
    ProjectAdmins:
      'Project admins can view any record and perform any action in CIMS Online for the project',
    Users: {
      ID: 'A unique identifier used to reference the user in CIMS and Azure Active Directory',
      GivenName: 'The given name of the user in Azure Active Directory',
      Surname: 'The surname of user in Azure Active Directory',
      DisplayName: "User's name displayed in modules",
      Email: "User's email address Notifications will be sent to",
      UserGroups: 'Project Azure Groups User pulled from',
      Roles: 'Permission Roles assigned to User',
      ProjectAdmins:
        'Project admins can view any record and perform any action in CIMS Online for the project',
      RolesColumn:
        'Permission Roles assigned to User. All assigned Permissions can be viewed in the expandable section.',
      GreyTags:
        'Role Permissions are grey, and are assigned to the User through one or more Roles.',
      BlueTags:
        'Additional Permissions are blue, and are explicitly assigned to the User in addition to their Role Permissions.',
      PurpleTags:
        'Project Admins and Developers do not need explicit permissions',
      ExplicitPermissions:
        'Displays Permissions assigned explicitly to the User.',
      ImplicitPermissions:
        'Displays Permissions granted to the User through membership in groups, disciplines, etc. Project Admins and Developers are always implicitly authorized.',
      Permissions:
        'Role Permissions and Additional Permissions assigned to User',
    } as const,
    Roles: {
      Name: 'Name used to identify group of Permissions',
      Permissions: 'Group of assigned Permissions',
    } as const,
    CustomFields: {
      Name: 'Name to display in tables which use Custom Field',
      AlwaysVisible:
        'Shows, and allows editors to edit, values associated with inactive Custom Fields on associated records',
      DataType:
        'The data type affects how the value will be set on associated records',
      ClassTypes: (options?: { header: boolean }) =>
        `Database objects which support Custom Fields${
          options?.header
            ? '. Object specific settings can be configured in the expandable section'
            : ''
        }` as const,
      Editors:
        'Editors are authorized to edit the values of Custom Fields on records associated with the record type. All Users are editors unless specified',
      AlwaysEditable:
        "Custom Field values can be edited, even when an associated record's status would mark it as closed",
      Required:
        'Custom Field values must be set before an associated record can be created or edited',
      Prominent:
        'This Custom Field should always appear in table headers and rows, rather than expandable sections',
      RecordLinkTypes:
        "Database objects to create a hyperlink to, from the Custom Field's associated record",
      Actions:
        'Custom Field values are unaffected when a Custom Field is removed',
      Timestamp: 'Values should include the time',
      Reports: 'Generate a specific PDF Report on demand',
      Items:
        'Pre-defined item values to populate in a dropdown when setting the Custom Field value',
      Style: 'Controls how editors interact with items',
    } as const,
    NonUserEmails: {
      Label: 'Name displayed in Modules',
      Email: 'Email Address Notifications will be sent to',
    } as const,
    ProjectSettings: {
      Settings: {
        Logo: 'Added to the top left corner of all reports. Logos are set to 100 pixels at their widest point and should be resized before upload to look good at this scale',
        CMProjectLayer:
          'Used in PDF Design Layers to configure visible project layers',
        EditModalPopConfirm: ({
          clearingLayerId,
          clearingLogo,
        }: {
          clearingLayerId?: boolean
          clearingLogo?: boolean
        }) =>
          `Are you sure you want to clear ${
            clearingLayerId ? 'CM Project Layer ID' : ''
          }${clearingLayerId && clearingLogo ? ' and ' : ''}${
            clearingLogo ? 'Project Logo' : ''
          }?`,
        PromptDesignSubmittal:
          'When a Field Design Change is submitted with a Concurrence Level which requires a Design Submittal, the user will be prompted to create a linked Design Submittal',
        ReplaceSpaces:
          'Replace any space characters found in a Photo or Report Filename with a dash character',
      } as const,
      SharePoint: {
        Instructions:
          'Complete SharePoint URL is the link used when clicking on SharePoint in left navigation bar. SharePoint Site URL is the default link used. SharePoint Landing Page can be used to redirect to a different site page on the default SharePoint site. To use, go to the page you want to redirect to and copy the portion of the url after what is shown here in the SharePoint Site URL',
        SiteURL: 'Default URL, base URL for Complete SharePoint URL',
        LandingPage:
          'Appended to SharePoint Site URL to create Complete SharePoint URL',
        CompleteURL:
          'Composed by appending SharePoint Landing Page to the end of SharePoint Site URL. Used as SharePoint link in navigation bar',
        CustomDrive: `A Document Library to use as the application's Document Library. You should only change this if you know what you're doing`,
        CasPacketAttachmentOrder:
          'Determines how images and PDFs are ordered when a Construction Admin Submittal packet is automatically created after the submittal is closed',
      } as const,
      IFITracker: {
        ImportConfigurator:
          'Configure which reports should periodically create and update associated Identified Field Issues in the IFI Tracker',
        Enabled:
          'When enabled, will attempt to create Identified Field Issues for records of this report type if: They fail any associated checks AND were created after the Effective Date',
        EffectiveDate:
          'Only attempt to issue an Identified Field Issue for this report type if the record was created after this date',
        MinAmbientTemp:
          'The minimum ambient air temperature threshold to validate Concrete Test concrete temperatures in Fahrenheit',
        TempRange:
          'The allowable range for Concrete Test concrete temperatures in Fahrenheit',
        CompactionLevel:
          'Generate an IFI if a single Test fails, or the Lot average fails',
        /**
         * @returns
         * ```typescript
         * // without options
         * `This message will populate the 'description' of the Identified Field Issue
         *  generated when this check fails`
         *
         * // with options
         * `This message will populate the 'description' of the Identified Field Issue
         *  generated when a ${reportType} is ${reason}`
         * ```
         */
        FailureMessage: (options?: {
          reportType: IFIReportTypeItem['type']
          reason: string
        }) =>
          `This message will populate the 'description' of the Identified Field Issue generated when ${
            !!options
              ? `a ${options.reportType} is ${options.reason}`
              : 'this check fails'
          }`,
      },
    } as const,
  } as const,
  IdentifiedFieldIssues: {
    AssociatedRecordNumber: (entity: string) =>
      `${entity}s created from, or associated with, the Identified Field Issue`,
    IFITracker: {
      ConcreteTests: {
        Technician: 'Technician who tested the concrete',
        ConcreteTempTestedBy: 'The technician who tested the concrete temp',
      } as const,
      Inspections: {
        WorkType: 'The type of work associated with this report',
        Technician: 'Technician who performed the inspection',
      } as const,
      PunchLists: {
        StatusUpdate: 'Comments made by contractor',
        Technician: 'Technician who performed the inspection',
      } as const,
      ControlPoints: {
        WorkType: 'The type of work associated with this report',
        Technician: 'Technician who performed the inspection',
      } as const,
      DensityLots: {
        ProctorValue: 'The maximum dry density at the optimum moisture',
        AverageCompaction: 'The Average Compaction for all tests in the Lot',
        LotCompactionRequirement:
          'The minimum Compaction required based on Material type',
        Technician: 'Technician who created the lot',
      } as const,
      DensityTests: {
        AveragePercent: 'Average Compaction based on readings for this Test',
        Technician: 'Technician who created the density test',
      } as const,
      Status: 'The current workflow status of the Identified Field Issue',
      ReportType: "The original report's type",
      ReportDate: 'The date of the original report',
      ReviewedBy: 'The user who reviewed the Identified Field Issue',
      ReviewNotes: 'Notes associated with the Identified Field Issue',
      ReportID: 'The human readable identifier of the original report',
      ReviewStatus: 'The review status of the original report',
      SuggestedIssueType:
        'Issue Types which should be considered when creating a new Issue from the Identified Field Issue',
      InspectionNotes: 'Notes associated with the original report',
      TestingNotes: "Notes associated with the original report's test",
      ReportCreatedDate: 'The date the original report was created',
      ReportSubmittedDate: 'The date the original report was submitted',
      ReportReviewedDate: 'The date the original report was reviewed',
      ReportReviewedBy: 'The technician who reviewed the original report',
      ReportLastEditedDate: 'The last date the original report was edited',
      IssueReviewDate: 'The date the Identified Field Issue was reviewed',
      IssueDescription:
        'The description of the original issue. If changed since the Identified Field Issue was created, the original value will be displayed as well',
    } as const,
    Notifications: {
      IssueSummarized: 'Recipients recieve a summary email',
      Frequency: (type?: FrequencyItem['type']) => {
        const IS = 'Issue Summarized'
        const INTRO = `An ${IS} email will be sent out`
        if (type === FrequencyItem.MinuteFrequencyItem.type)
          return `${INTRO} on an interval, every 15, 30, or 45 minutes`
        else if (type === FrequencyItem.HourlyFrequencyItem.type)
          return `${INTRO} on an interval, every 1 to 23 hours`
        else if (type === FrequencyItem.DailyFrequencyItem.type)
          return `${INTRO} every day at the chosen time`
        else if (type === FrequencyItem.WeeklyFrequencyItem.type)
          return `${INTRO} every week on the chosen day, at the chosen time`
        else return `How often an email should be sent out for ${IS}`
      },
      Day: 'The day of the week an Issue Summarized email will be sent out',
      Time: (type?: FrequencyItem['type']) => {
        if (type === FrequencyItem.MinuteFrequencyItem.type)
          return 'Minutes between emails'
        else if (type === FrequencyItem.HourlyFrequencyItem.type)
          return 'Hours between emails'
        else if (
          type === FrequencyItem.DailyFrequencyItem.type ||
          type === FrequencyItem.WeeklyFrequencyItem.type
        )
          return 'Time of day the email will be sent out'
        else return ''
      },
      ByIFIType: ({ name }: Pick<NotificationEventItem, 'name'>) =>
        `Recipients receive an email for ${name} events for IFIs with a specific Report Type` as const,
    } as const,
  } as const,
  Materials: {
    Asphalt: {
      Percentage: 'The target percentage of a Mix Design',
      MixID: 'The Mix Design identification number',
      Supplier: 'The company supplying the asphalt',
      MixDesignsCount:
        'The number of Asphalt Mix Designs associated with this Target',
      MixDesignPercentages:
        'Asphalt Mix Designs associated with this Target and their associated percentages',
      TargetsCount:
        'The number of Targets associated with this Asphalt Mix Design',
      TargetPercentages:
        'Targets associated with this Asphalt Mix Design and their associated percentages',
      Required:
        'Required Targets must have a Percentage set to save an associated Mix Design',
    },
    Concrete: {
      ...ConcreteMixDesign,
    },
  },
  EngineeringJudgements: {
    Types: {
      Name: 'Engineering Judgements',
      AllowanceDescription:
        'Engineering Judgement allowance description, this is auto-populated',
      ReportType:
        'Enter the applicable Inspection(s), Control Point(s), or any other reference(s)',
      Issuers: 'Who is allowed to issue the Engineering Judgement',
      AssociatedJudgements:
        'Engineering Judgements that have been assigned this Type',
    } as const,
    EJLog: {
      Status: 'Current workflow status of the Engineering Judgement',
      Number: 'Engineering Judgement identification number',
      Type: 'What type of Engineering Judgement is being issued',
      FailureDate: 'Date failure occurred',
      Reports:
        'Reports that reference the failure. If Concrete Gradation, manually enter, otherwise select from dropdown',
      FailureDescription: "Description of why it's considered failing",
      Justification: 'Explain why the Engineering Judgement is justifiable',
      IssuedBy: 'Who issued the Engineering Judgement',
      IssuedDate: 'The day the Engineering Judgement was issued',
      Notes: 'Add any notes that further support the Engineering Judgement',
      AllowanceDescription:
        'Engineering Judgement allowance description, this is auto-populated',
      PayItems: 'Pay Item(s) associated with this Engineering Judgement',
    } as const,
  } as const,
} as const
